import React from "react";

/**
 * AfriMed Focus chip
 *
 * Reusable pill used for:
 * - Doctor “Your Focus” chips
 * - Filter chips on DoctorProfile / Home / Appointments
 *
 * Behaviour:
 * - By default: simple clickable pill (no X)
 * - If `removable` is true, show a small “×” badge on the right
 *   and call `onRemove` when that badge is tapped.
 */
export default function FocusChip({
  label,
  removable = false,
  disabled = false,
  onClick,
  onRemove,
  className = "",
}) {
  const handleRemove = (e) => {
    e.stopPropagation(); // don’t trigger main onClick
    if (disabled) return;
    onRemove?.();
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={[
        "inline-flex items-center justify-center",
        "h-8 px-3 rounded-lg",
        // tonal AfriMed blue chip (matches DoctorProfile / blueprints)
        "bg-[rgba(77,163,255,0.12)]",
        "text-label-large text-[rgb(var(--afrimed-primary,0,97,255))]",
        "whitespace-nowrap gap-2",
        "disabled:opacity-60 disabled:cursor-default",
        "transition-transform duration-120 active:scale-[0.97]",
        "focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-[rgba(77,163,255,0.45)] focus-visible:ring-offset-2",
        className,
      ].join(" ")}
      style={{
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span className="leading-[18px]">{label}</span>

      {removable && (
        <span
          role="button"
          aria-label={`Remove ${label}`}
          onClick={handleRemove}
          className="
            inline-flex items-center justify-center
            w-4 h-4 rounded-full
            bg-[rgba(77,163,255,0.18)]
            text-[11px] leading-none
          "
        >
          ×
        </span>
      )}
    </button>
  );
}